import { useState } from 'react';
import { 
    useContractsByClientQuery, 
    useActiveContractQuery, 
    useUploadContractMutation, 
    useUploadSignatureMutation,
    useArchiveContractMutation 
} from '../services/hooks/useContratos';
import { validatePdfFile, validateSignatureFile } from '../services/api/contractService';
import { useUser } from '@clerk/clerk-react';

const TestContractsPage = () => {
    const { user } = useUser();
    const [testClientId, setTestClientId] = useState(''); // Cliente para pruebas
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [signatureFile, setSignatureFile] = useState<File | null>(null);
    const [firmanteDni, setFirmanteDni] = useState('');
    const [testSubscriptionId, setTestSubscriptionId] = useState(''); // Suscripción para pruebas

    // Hooks de React Query
    const contractsQuery = useContractsByClientQuery(testClientId || undefined);
    const activeContractQuery = useActiveContractQuery(testClientId || undefined);
    
    // Mutations
    const uploadContractMutation = useUploadContractMutation();
    const uploadSignatureMutation = useUploadSignatureMutation();
    const archiveContractMutation = useArchiveContractMutation();

    // Handlers
    const handleUploadContract = () => {
        if (!selectedFile || !testClientId || !testSubscriptionId) {
            alert('Faltan datos: archivo PDF, Cliente ID y Suscripción ID');
            return;
        }

        const validation = validatePdfFile(selectedFile);
        if (!validation.isValid) {
            alert(`Error: ${validation.error}`);
            return;
        }

        uploadContractMutation.mutate({
            contractFile: selectedFile,
            clientId: testClientId,
            subscriptionId: testSubscriptionId,
            createdBy: user?.id || 'test-user'
        });
    };

    const handleUploadSignature = (contractId: string) => {
        if (!signatureFile || !firmanteDni) {
            alert('Faltan datos: archivo de firma y DNI del firmante');
            return;
        }

        const validation = validateSignatureFile(signatureFile);
        if (!validation.isValid) {
            alert(`Error: ${validation.error}`);
            return;
        }

        uploadSignatureMutation.mutate({
            contractId,
            signatureFile,
            firmanteDni,
            updatedBy: user?.id || 'test-user'
        });
    };

    const handleArchiveContract = (contractId: string) => {
        if (!confirm('¿Estás seguro de archivar este contrato?')) return;

        archiveContractMutation.mutate({
            contractId,
            updatedBy: user?.id || 'test-user',
            reason: 'Prueba de archivado desde componente de testing'
        });
    };

    return (
        <div className="container pt-24 pb-24 w-full">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h1 className="text-2xl font-bold text-yellow-800 mb-2">
                    🧪 Página de Pruebas - Contratos Digitales
                </h1>
                <p className="text-yellow-700">
                    Componente temporal para probar la integración JWT + API + React Query
                </p>
            </div>

            {/* Configuración de prueba */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">📋 Configuración de Prueba</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cliente ID (para pruebas)
                        </label>
                        <input
                            type="text"
                            value={testClientId}
                            onChange={(e) => setTestClientId(e.target.value)}
                            placeholder="Ingresa un ID de cliente existente"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Suscripción ID (para pruebas)
                        </label>
                        <input
                            type="text"
                            value={testSubscriptionId}
                            onChange={(e) => setTestSubscriptionId(e.target.value)}
                            placeholder="Ingresa un ID de suscripción existente"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Estado de autenticación */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">🔐 Estado de Autenticación</h2>
                <div className="space-y-2">
                    <p><strong>Usuario:</strong> {user?.emailAddresses[0]?.emailAddress || 'No autenticado'}</p>
                    <p><strong>User ID:</strong> {user?.id || 'N/A'}</p>
                    <p className="text-sm text-gray-600">
                        ℹ️ Abre DevTools → Network para ver el header "Authorization: Bearer ..." automático
                    </p>
                </div>
            </div>

            {/* Upload de contrato */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">📄 Subir Contrato (PDF)</h2>
                <div className="space-y-4">
                    <div>
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {selectedFile && (
                            <p className="mt-2 text-sm text-gray-600">
                                Archivo: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleUploadContract}
                        disabled={!selectedFile || !testClientId || !testSubscriptionId || uploadContractMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {uploadContractMutation.isPending ? 'Subiendo...' : 'Subir Contrato'}
                    </button>
                </div>
            </div>

            {/* Contrato activo */}
            {testClientId && (
                <div className="bg-white shadow rounded-lg p-6">
                    <h2 className="text-lg font-semibold mb-4">🎯 Contrato Activo</h2>
                    {activeContractQuery.isLoading && <p>Cargando contrato activo...</p>}
                    {activeContractQuery.error && (
                        <p className="text-red-600">Error: {(activeContractQuery.error as any)?.message}</p>
                    )}
                    {activeContractQuery.data ? (
                        <div className="bg-green-50 p-4 rounded border">
                            <p><strong>ID:</strong> {activeContractQuery.data._id}</p>
                            <p><strong>Estado:</strong> <span className="capitalize font-medium">{activeContractQuery.data.estado}</span></p>
                            <p><strong>Firmante:</strong> {activeContractQuery.data.firmanteNombre} (DNI: {activeContractQuery.data.firmanteDni})</p>
                            <p><strong>Versión:</strong> {activeContractQuery.data.version}</p>
                            
                            {/* Firma del contrato */}
                            {activeContractQuery.data.estado === 'pendiente' && (
                                <div className="mt-4 space-y-3">
                                    <h3 className="font-semibold">✍️ Firmar Contrato</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input
                                            type="text"
                                            placeholder="DNI del firmante"
                                            value={firmanteDni}
                                            onChange={(e) => setFirmanteDni(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setSignatureFile(e.target.files?.[0] || null)}
                                            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                                        />
                                    </div>
                                    <button
                                        onClick={() => handleUploadSignature(activeContractQuery.data!._id)}
                                        disabled={!signatureFile || !firmanteDni || uploadSignatureMutation.isPending}
                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                        {uploadSignatureMutation.isPending ? 'Firmando...' : 'Subir Firma'}
                                    </button>
                                </div>
                            )}
                            
                            {/* Archivar contrato */}
                            <div className="mt-4">
                                <button
                                    onClick={() => handleArchiveContract(activeContractQuery.data!._id)}
                                    disabled={archiveContractMutation.isPending}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    {archiveContractMutation.isPending ? 'Archivando...' : 'Archivar Contrato'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-gray-600">No hay contrato activo para este cliente</p>
                    )}
                </div>
            )}

            {/* Lista de contratos */}
            {testClientId && (
                <div className="bg-white shadow rounded-lg p-6">
                    <h2 className="text-lg font-semibold mb-4">📋 Todos los Contratos</h2>
                    {contractsQuery.isLoading && <p>Cargando contratos...</p>}
                    {contractsQuery.error && (
                        <p className="text-red-600">Error: {(contractsQuery.error as any)?.message}</p>
                    )}
                    {contractsQuery.data && (
                        <div>
                            <p className="mb-4 text-gray-600">
                                Total: {contractsQuery.data.count} contratos
                            </p>
                            <div className="space-y-3">
                                {contractsQuery.data.contracts.map((contract) => (
                                    <div key={contract._id} className="border border-gray-200 rounded p-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p><strong>ID:</strong> {contract._id}</p>
                                                <p><strong>Estado:</strong> 
                                                    <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${
                                                        contract.estado === 'firmado' ? 'bg-green-100 text-green-800' :
                                                        contract.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {contract.estado}
                                                    </span>
                                                </p>
                                                <p><strong>Versión:</strong> {contract.version}</p>
                                                <p><strong>Activo:</strong> {contract.activo ? '✅ Sí' : '❌ No'}</p>
                                            </div>
                                            <div className="text-right text-sm text-gray-500">
                                                <p>Creado: {new Date(contract.createdAt).toLocaleDateString()}</p>
                                                {contract.fechaFirma && (
                                                    <p>Firmado: {new Date(contract.fechaFirma).toLocaleDateString()}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Debug info */}
            <div className="bg-gray-50 shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">🐛 Debug Info</h2>
                <div className="text-sm space-y-2 font-mono">
                    <p><strong>API Base URL:</strong> {import.meta.env.VITE_API_URL}</p>
                    <p><strong>Mutations Status:</strong></p>
                    <ul className="ml-4 space-y-1">
                        <li>Upload Contract: {uploadContractMutation.isPending ? '⏳ Loading' : uploadContractMutation.isSuccess ? '✅ Success' : uploadContractMutation.isError ? '❌ Error' : '⚪ Idle'}</li>
                        <li>Upload Signature: {uploadSignatureMutation.isPending ? '⏳ Loading' : uploadSignatureMutation.isSuccess ? '✅ Success' : uploadSignatureMutation.isError ? '❌ Error' : '⚪ Idle'}</li>
                        <li>Archive Contract: {archiveContractMutation.isPending ? '⏳ Loading' : archiveContractMutation.isSuccess ? '✅ Success' : archiveContractMutation.isError ? '❌ Error' : '⚪ Idle'}</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TestContractsPage;